import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Clock, MessageSquare } from 'lucide-react';
import { Evaluator } from '@/types/evaluator';

interface EvaluatorsTestProps {
  evaluators: Evaluator[];
  updating: string | null;
  onToggleStatus: (evaluatorId: string, field: 'evaluationCompleted' | 'contextualized') => void;
}

export default function EvaluatorsTest({ 
  evaluators, 
  updating, 
  onToggleStatus 
}: EvaluatorsTestProps) {
  return (
    <div className="space-y-4">
      {evaluators.map((evaluator) => (
        <motion.div
          key={evaluator.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {evaluator.firstName} {evaluator.lastName}
              </h3>
              <p className="text-gray-600">{evaluator.email}</p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
              <div className="flex items-center space-x-4">
                <div className={`flex items-center space-x-2 ${
                  evaluator.evaluationCompleted 
                    ? 'text-green-600' 
                    : 'text-amber-600'
                }`}>
                  {evaluator.evaluationCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Clock className="w-5 h-5" />
                  )}
                  <span className="text-sm font-medium">
                    {evaluator.evaluationCompleted ? 'Completed' : 'Pending'}
                  </span>
                </div>

                <button
                  onClick={() => onToggleStatus(evaluator.id, 'evaluationCompleted')}
                  disabled={updating === evaluator.id}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    updating === evaluator.id
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : evaluator.evaluationCompleted
                      ? 'bg-red-50 text-red-600 hover:bg-red-100'
                      : 'bg-green-50 text-green-600 hover:bg-green-100'
                  }`}
                >
                  {updating === evaluator.id
                    ? 'Updating...'
                    : evaluator.evaluationCompleted
                    ? 'Mark as Pending'
                    : 'Mark as Completed'}
                </button>
              </div>

              <div className="flex items-center space-x-4">
                <div className={`flex items-center space-x-2 ${
                  evaluator.contextualized 
                    ? 'text-green-600' 
                    : 'text-amber-600'
                }`}>
                  {evaluator.contextualized ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <MessageSquare className="w-5 h-5" />
                  )}
                  <span className="text-sm font-medium">
                    {evaluator.contextualized ? 'Context Added' : 'No Context'}
                  </span>
                </div>

                <button
                  onClick={() => onToggleStatus(evaluator.id, 'contextualized')}
                  disabled={updating === evaluator.id}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    updating === evaluator.id
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : evaluator.contextualized
                      ? 'bg-red-50 text-red-600 hover:bg-red-100'
                      : 'bg-green-50 text-green-600 hover:bg-green-100'
                  }`}
                >
                  {updating === evaluator.id
                    ? 'Updating...'
                    : evaluator.contextualized
                    ? 'Remove Context'
                    : 'Add Context'}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      ))}

      {evaluators.length === 0 && (
        <div className="text-center py-12 text-gray-600">
          No evaluators found. Add some evaluators first.
        </div>
      )}
    </div>
  );
}